import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../stores/auth-store';
import { useEnterpriseDrillContext } from '../../contexts/EnterpriseDrillContext';
import { useWfmMonthParam } from '../wfm/wfm-month-gate';
import useWfmLanding from '../../hooks/use-wfm-landing';
import { coverageSubtitle } from '../wfm/wfm-cells';
import WfmBreadcrumb from '../wfm/wfm-breadcrumb';

const ROUTE_LABELS = {
  'my-dashboard': 'My Dashboard',
  'enterprise': 'Enterprise',
  'direct-reports': 'Direct Reports',
  'my-outcomes': 'My Outcomes',
  'manager-review': 'Manager Review',
  'org-outcomes': 'Org Outcomes',
  'create-outcome': 'Create Outcome',
  'checkin': 'Record Check-in',
  'alerts': 'Alerts & Thresholds',
  'export-reports': 'Export & Reports',
  'token-forecast': 'Token Forecast',
  'manage-budget': 'Manage Budget',
  'person-search': 'Person Search',
  'delivery-teams': 'Delivery Teams',
  'cost-optimization': 'Cost Optimization',
  'adoption-trends': 'Adoption Trends',
  'admin': 'Admin',
  'users': 'User Management',
  'outcome': 'Outcome Detail',
  'approval': 'Approval Review',
};

function DeliveryTeamsCoverage({ color }) {
  const month = useWfmMonthParam();
  const { data } = useWfmLanding({ month, enabled: false });
  if (!data?.counts) return null;
  return (
    <span style={{ marginLeft: 16, color, fontSize: 15, fontFamily: 'var(--font-mono)' }}>
      {coverageSubtitle(data.counts)}
    </span>
  );
}

export default function BreadcrumbBar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { userProfile, userEmail } = useAuth();
  const { path, viewMode, navigateTo, resetToTop, isEnterpriseRoute, selectedPortfolioName } = useEnterpriseDrillContext();

  const segments = location.pathname.split('/').filter(Boolean);
  const orgDisplay = userProfile?.portfolio || userProfile?.delivery_stream || 'Organization';

  const lastSeg = segments[segments.length - 1];
  const parentSeg = segments.length > 1 ? segments[segments.length - 2] : null;
  const isManagerCheckin = segments.includes('manager-review') && segments.includes('checkin');
  const pageLabel = ROUTE_LABELS[lastSeg]
    || (isManagerCheckin ? 'Review Check-in' : null)
    || (parentSeg && ROUTE_LABELS[parentSeg] ? ROUTE_LABELS[parentSeg] : null)
    || lastSeg?.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
    || 'Dashboard';

  const wrapperStyle = {
    padding: '5px 24px',
    display: 'flex',
    alignItems: 'center',
    minHeight: 30,
    gap: 4,
    flexWrap: 'wrap',
    background: 'var(--theme-cardBg, #FFFFFF)',
    borderBottom: '1px solid var(--theme-border, #D9D2C4)',
    flexShrink: 0,
  };

  const linkColor = '#0A6E7C';
  const textColor = 'var(--theme-text, #1a1a1a)';
  const mutedColor = 'var(--theme-textTertiary, #888)';

  if (isEnterpriseRoute) {
    const isIndividual = viewMode === 'individual';
    const rootOrg = 'Enterprise';
    const displayOrg = selectedPortfolioName || rootOrg;

    if (path.length === 0) {
      return (
        <div style={wrapperStyle}>
          {selectedPortfolioName ? (
            <>
              <span style={{ color: linkColor, cursor: 'pointer', fontSize: 15, fontWeight: 500 }} onClick={() => {}}>{rootOrg}</span>
              <span style={{ color: mutedColor, fontSize: 15, margin: '0 6px' }}>/</span>
              <span style={{ fontWeight: 700, fontSize: 15, color: textColor }}>{selectedPortfolioName}</span>
              <span style={{ color: mutedColor, fontSize: 15, marginLeft: 8 }}>Portfolio</span>
            </>
          ) : (
            <span style={{ fontWeight: 700, fontSize: 15, color: textColor }}>{rootOrg}</span>
          )}
        </div>
      );
    }

    return (
      <div style={wrapperStyle}>
        <span
          onClick={resetToTop}
          style={{ color: linkColor, cursor: 'pointer', fontSize: 15, fontWeight: 500 }}
        >
          {rootOrg}
        </span>

        {path.map((item, idx) => (
          <React.Fragment key={item.id || idx}>
            <span style={{ color: mutedColor, fontSize: 15, margin: '0 6px' }}>/</span>
            <span
              onClick={() => idx < path.length - 1 && navigateTo(idx)}
              style={{
                color: idx < path.length - 1 ? linkColor : textColor,
                cursor: idx < path.length - 1 ? 'pointer' : 'default',
                fontWeight: idx === path.length - 1 ? 700 : 500,
                fontSize: 15,
              }}
            >
              {item.name}
            </span>
          </React.Fragment>
        ))}

        {isIndividual && (
          <span style={{ marginLeft: 16, color: mutedColor, fontSize: 15 }}>
            Individual view
          </span>
        )}
      </div>
    );
  }

  const isDRIndividual = segments[0] === 'direct-reports' && segments[1] === 'individual';
  if (isDRIndividual) {
    const drName = location.state?.name || segments[2] || 'Individual';
    return (
      <div style={wrapperStyle}>
        <span
          onClick={() => navigate('/direct-reports')}
          style={{ color: linkColor, cursor: 'pointer', fontSize: 15, fontWeight: 500 }}
        >
          {userProfile?.display_name || userEmail?.split('@')[0] || 'User'}
        </span>
        <span style={{ color: mutedColor, fontSize: 15, margin: '0 6px' }}>/</span>
        <span style={{ fontWeight: 700, fontSize: 15, color: textColor }}>{drName}</span>
        <span style={{ marginLeft: 16, color: mutedColor, fontSize: 15 }}>Individual view</span>
      </div>
    );
  }

  const isPersonalPage = ['my-dashboard', 'my-outcomes', 'manager-review', 'direct-reports'].includes(segments[0]);
  const primaryLabel = isPersonalPage
    ? (userProfile?.display_name || (userEmail ? userEmail.split('@')[0] : 'User'))
    : orgDisplay;

  if (isManagerCheckin) {
    const personName = location.state?.personName;
    return (
      <div style={wrapperStyle}>
        <span
          onClick={() => navigate('/manager-review')}
          style={{ color: linkColor, cursor: 'pointer', fontSize: 15, fontWeight: 500 }}
        >
          Manager Review
        </span>
        {personName && (
          <>
            <span style={{ color: mutedColor, fontSize: 15, margin: '0 6px' }}>/</span>
            <span style={{ color: mutedColor, fontSize: 15 }}>{personName}</span>
          </>
        )}
        <span style={{ fontWeight: 700, fontSize: 15, color: textColor, marginLeft: 8 }}>
          Review Check-in
        </span>
      </div>
    );
  }

  if (segments[0] === 'delivery-teams' && ['nodes', 'teams'].includes(segments[1]) && segments[2]) {
    return (
      <div style={wrapperStyle}>
        <WfmBreadcrumb
          kind={segments[1]} id={decodeURIComponent(segments[2])}
          linkColor={linkColor} textColor={textColor} mutedColor={mutedColor}
        />
      </div>
    );
  }

  const STANDALONE_PAGES = ['create-outcome', 'person-search', 'delivery-teams'];
  if (STANDALONE_PAGES.includes(segments[0])) {
    return (
      <div style={wrapperStyle}>
        <span style={{ fontWeight: 700, fontSize: 15, color: textColor }}>{ROUTE_LABELS[segments[0]] || pageLabel}</span>
        {segments.length === 1 && segments[0] === 'delivery-teams' && <DeliveryTeamsCoverage color={mutedColor} />}
      </div>
    );
  }

  return (
    <div style={wrapperStyle}>
      <span style={{ fontWeight: 700, fontSize: 15, color: textColor }}>{primaryLabel}</span>
      <span style={{ color: mutedColor, fontSize: 15, marginLeft: 8 }}>{pageLabel}</span>
    </div>
  );
}
